/**
 * useNavigateKeepParams
 *
 * Wraps React Router's `useNavigate` so that designated override URL
 * parameters (?mocks=true, ?dqx=pass|fail) are
 * automatically carried forward on every route change.
 *
 * Also exports `useLinkWithParams` for building `<Link to={...}>` props
 * that preserve the same params.
 */

import { useCallback } from "react";
import { useNavigate, useSearchParams, type NavigateOptions } from "react-router-dom";

/** URL param keys that should persist across route navigations */
const PERSISTENT_PARAMS = ["mocks", "dqx"] as const;

/**
 * Read persistent override params from the current URL.
 * Returns a URLSearchParams containing only the keys we want to keep.
 */
function getPersistentParams(current: URLSearchParams): URLSearchParams {
  const kept = new URLSearchParams();
  for (const key of PERSISTENT_PARAMS) {
    const val = current.get(key);
    if (val !== null) kept.set(key, val);
  }
  return kept;
}

/**
 * Build a path string with persistent params appended.
 * If the target path already contains a query string, merges them.
 */
function buildPathWithParams(path: string, current: URLSearchParams): string {
  const kept = getPersistentParams(current);
  if (kept.size === 0) return path;

  const [base, existingQuery] = path.split("?");
  const merged = new URLSearchParams(existingQuery ?? "");
  for (const [k, v] of kept) {
    if (!merged.has(k)) merged.set(k, v);
  }
  const qs = merged.toString();
  return qs ? `${base}?${qs}` : (base ?? path);
}

/**
 * Drop-in replacement for `useNavigate()` that preserves override params.
 *
 * Usage:
 *   const navigate = useNavigateKeepParams();
 *   navigate("/pipeline");          // ?mocks=true carries forward
 *   navigate("/", { replace: true });
 */
export function useNavigateKeepParams() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(
    (to: string, options?: NavigateOptions) => {
      navigate(buildPathWithParams(to, searchParams), options);
    },
    [navigate, searchParams]
  );
}

/**
 * Build a `to` string for `<Link>` that includes persistent params.
 *
 * Usage in components:
 *   const linkTo = useLinkWithParams();
 *   <Link to={linkTo("/pipeline")}>Pipeline</Link>
 */
export function useLinkWithParams() {
  const [searchParams] = useSearchParams();

  return useCallback((path: string) => buildPathWithParams(path, searchParams), [searchParams]);
}
